import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ArrowLeft, GitBranch, Key, Package, Copy, Check, Plus, Settings } from "lucide-react";
import { useDeployments } from "@/features/deployments/hooks/useDeployments";
import { useAppAccess } from "@/features/apps/hooks/useApps";
import { CreateDeploymentDialog } from "@/features/deployments/components/CreateDeploymentDialog";

export const Route = createFileRoute("/_authenticated/apps/$appName")({
  component: AppDetailPage,
});

function AppDetailPage() {
  const { appName } = Route.useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useDeployments(appName);
  const access = useAppAccess(appName);
  const [createOpen, setCreateOpen] = useState(false);

  const deployments = data?.deployments ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      {/* Back + Title */}
      <div className="flex items-center gap-4">
        <Link
          to="/apps"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
        >
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-heading">{appName}</h1>
        </div>
        <Link
          to="/apps/$appName/settings"
          params={{ appName }}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3.5 py-2 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
        >
          <Settings size={14} />
          {t("apps.settings")}
        </Link>
      </div>

      {/* Deployments section */}
      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-heading">{t("deployments.title")}</h2>
          {access.can("deployments.manage") && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3.5 py-2 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
            >
              <Plus size={14} />
              {t("deployments.createDeployment")}
            </button>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo border-t-transparent" />
            <p className="text-sm text-muted">{t("common.loading")}</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger"
          >
            {error.error ?? t("common.error")}
          </div>
        )}

        {/* Deployments list */}
        {!isLoading && !error && (
          <div className="mt-4 space-y-3">
            {deployments.map((dep) => (
              <DeploymentCard
                key={dep.name}
                appName={appName}
                deployment={dep}
                showKey={access.can("deploymentKeys.read")}
              />
            ))}
          </div>
        )}
      </section>

      <CreateDeploymentDialog
        appName={appName}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeploymentCard
// ---------------------------------------------------------------------------

interface DeploymentCardProps {
  appName: string;
  deployment: {
    name: string;
    key?: string;
    package?: {
      appVersion?: string;
      label?: string;
      description?: string;
    };
  };
  showKey: boolean;
}

function DeploymentCard({ appName, deployment, showKey }: DeploymentCardProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const pkg = deployment.package;

  const copyKey = async () => {
    if (!deployment.key) return;
    await navigator.clipboard.writeText(deployment.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-5 transition-all hover:border-border-light">
      <div className="flex items-start justify-between">
        {/* Name + key */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo/10 to-cyan/10">
            <GitBranch size={18} className="text-indigo" />
          </div>
          <div>
            <Link
              to="/apps/$appName/deployments/$deploymentName"
              params={{ appName, deploymentName: deployment.name }}
              className="text-sm font-semibold text-heading transition-colors hover:text-indigo"
            >
              {deployment.name}
            </Link>
            {showKey && deployment.key && deployment.key !== "(hidden)" && (
              <button
                type="button"
                onClick={copyKey}
                className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted transition-colors hover:text-heading"
                title={t("deployments.deploymentKey")}
              >
                <Key size={10} />
                <code className="font-mono">{deployment.key.slice(0, 16)}…</code>
                {copied ? <Check size={10} className="text-success" /> : <Copy size={10} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Current release */}
      <div className="mt-4 rounded-lg bg-elevated/50 px-4 py-3">
        {pkg ? (
          <div className="flex items-center gap-3 text-xs">
            <Package size={14} className="text-muted" />
            <span className="font-mono font-medium text-heading">{pkg.label ?? "—"}</span>
            <span className="text-muted">•</span>
            <span className="text-muted">v{pkg.appVersion ?? "?"}</span>
            {pkg.description && (
              <>
                <span className="text-muted">•</span>
                <span className="truncate text-muted">{pkg.description}</span>
              </>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted">{t("deployments.noRelease")}</p>
        )}
      </div>
    </div>
  );
}
